import { RetainedCapacity } from '../../components/RetainedCapacity';
import { WorkspacePlans } from '../../components/WorkspacePlans';
import { getPlan } from '../../config/plans';
/**
 * BillingDashboard.tsx — the tenant's Billing & Usage page (canonical /billing).
 *
 * Shows only what the server returns: GET /billing (plan + status + trial and
 * last-payment dates), GET /billing/usage (measured, tenant-scoped), and
 * GET /billing/invoices (Stripe invoices with hosted/PDF links). Checkout and
 * portal open Stripe via POST /billing/checkout and POST /billing/portal.
 *
 * Nothing is fabricated: a failed request shows an error with Retry (never a
 * Free plan, zero usage or "no invoices"), a null usage meter reads "Not
 * measured", and no price, trial length, quota or renewal date is shown
 * because the API does not return one. A ?checkout=success return is only
 * navigation context — the plan shown is the refreshed server state.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Settings, Zap, RotateCw, ArrowUpRight } from 'lucide-react';
import { apiClient, getErrorMessage } from '../../packages/api-client';
import { getBillingUsage, type BillingUsage } from '../../packages/api-client/billing-api';
import { usePageTitle } from '../../hooks/usePageTitle';
import { HubHeading, NoteBar } from '../../components/hub/Hub';

interface BillingInfo {
    id: string;
    name: string;
    plan: string;
    plan_version?: string;
    project_limit?: number | null;
    people_limit?: number | null;
    stripe_subscription_id?: string;
    billing_status: string; // 'free' | 'trial' | 'paid' | 'unpaid' | 'cancelled'
    stripe_customer_id?: string;
    trial_ends_at?: string;
    paid_at?: string;
}

interface Invoice {
    id: string;
    number?: string;
    created: number; // unix seconds
    amount_paid: number; // minor units
    amount_due: number;
    currency: string;
    status: string; // draft | open | paid | uncollectible | void
    hosted_invoice_url?: string;
    invoice_pdf?: string;
}

/** A remote value: not loaded yet, loaded, or failed with a message. */
type Remote<T> = { state: 'loading' } | { state: 'ok'; data: T } | { state: 'error'; message: string };

function formatInvoiceAmount(minor: number, currency: string) {
    const code = (currency || 'usd').toUpperCase();
    try {
        const fmt = new Intl.NumberFormat(undefined, { style: 'currency', currency: code });
        // Stripe amounts are in the currency's minor unit (JPY has none).
        return fmt.format(minor / 10 ** (fmt.resolvedOptions().maximumFractionDigits ?? 2));
    } catch {
        return `${minor} ${code} (minor units)`;
    }
}

function formatBytes(n: number) {
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let i = 0;
    let v = n;
    while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
    return `${i === 0 ? v : v.toFixed(1)} ${units[i]}`;
}

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString();

type Tone = 'ok' | 'warn' | 'danger' | undefined;

// Stripe invoice status → pill tone; draft/void/unknown stay neutral.
const INVOICE_TONE: Record<string, Tone> = { paid: 'ok', open: 'warn', uncollectible: 'danger' };

// Maps known Stripe error codes + types to short, user-facing messages. The
// backend returns {error, code, type, doc_url} for billing failures.
function friendlyBillingError(e: any, fallback: string): string {
    const data = e?.response?.data;
    if (data && typeof data === 'object') {
        switch (data.code) {
            case 'card_declined': return 'Your card was declined. Try a different payment method.';
            case 'expired_card': return 'That card has expired. Please use a different one.';
            case 'incorrect_cvc': return 'The security code (CVC) was incorrect.';
            case 'insufficient_funds': return 'The card has insufficient funds.';
            case 'processing_error': return 'Your card provider had a temporary problem — try again in a minute.';
        }
        if (data.type === 'rate_limit_error') return 'Too many requests. Please try again in a moment.';
        if (data.error) return data.error;
        if (data.message) return data.message;
    }
    if (typeof data === 'string' && data) return data;
    return e?.message || fallback;
}

// Server billing_status → badge. An unknown status is shown verbatim, never
// mapped to Free.
const STATUS_BADGE: Record<string, { label: string; tone: Tone }> = {
    free:      { label: 'Free',      tone: undefined },
    trial:     { label: 'Trialing',  tone: 'ok' },
    paid:      { label: 'Active',    tone: 'ok' },
    unpaid:    { label: 'Past due',  tone: 'danger' },
    cancelled: { label: 'Cancelled', tone: undefined },
};

const muted: React.CSSProperties = { color: 'var(--ink-muted)', fontSize: 'var(--font-size-sm)', margin: 0 };
const errorText: React.CSSProperties = { color: 'var(--danger-text)', fontSize: 'var(--font-size-sm)', margin: 0 };
const iconBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6 };

function Failed({ what, message, onRetry }: { what: string; message: string; onRetry: () => void }) {
    return (
        <div role="alert" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
            <p style={errorText}>Couldn't load {what}: {message}</p>
            <button className="btn btn-secondary btn-sm" onClick={onRetry} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <RotateCw size={14} aria-hidden="true" /> Retry
            </button>
        </div>
    );
}

function Loading({ what }: { what: string }) {
    return <div role="status" style={muted}><span className="spinner-sm" aria-hidden="true" /> Loading {what}…</div>;
}

/** Opens a Stripe session URL returned by the backend, with loading + inline error. */
function useStripeRedirect(path: string, body: object) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const open = async () => {
        setBusy(true);
        setError(null);
        try {
            const res = await apiClient.post<{ url: string }>(path, body);
            if (!res.data?.url) throw new Error('Stripe did not return a URL.');
            window.location.assign(res.data.url);
            // Stay busy: the page is navigating away.
        } catch (e) {
            setError(friendlyBillingError(e, 'Unknown error'));
            setBusy(false);
        }
    };
    return { busy, error, open };
}

function ReturnBanner({ checkout, info }: { checkout: string | null; info: BillingInfo | null }) {
    const box = (text: string) => <div role="status" className="hub-scope-notice">{text}</div>;
    if (checkout === 'cancelled') return box('Checkout was cancelled. No subscription was started.');
    if (checkout !== 'success') return null;
    // Only the server's (webhook-synced) state says the plan changed.
    const confirmed = info && ['team', 'pro'].includes(info.plan) && (info.billing_status === 'trial' || info.billing_status === 'paid');
    return confirmed
        ? box(`Stripe confirmed your subscription. Status: ${STATUS_BADGE[info.billing_status].label}.`)
        : box('Payment submitted. Your plan updates when Stripe confirms it — refresh this page in a moment.');
}

export function BillingDashboard() {
    usePageTitle('Billing');
    const [params] = useSearchParams();
    const checkout = params.get('checkout');

    const [info, setInfo] = useState<Remote<BillingInfo> | { state: 'forbidden' }>({ state: 'loading' });
    const [usage, setUsage] = useState<Remote<BillingUsage>>({ state: 'loading' });
    const [invoices, setInvoices] = useState<Remote<Invoice[]>>({ state: 'loading' });

    const [selectedPlan, setSelectedPlan] = useState('team');
    const checkoutSession = useStripeRedirect('/billing/checkout', { plan: selectedPlan });
    const portalSession = useStripeRedirect('/billing/portal', {});

    const loadUsage = useCallback(() => {
        setUsage({ state: 'loading' });
        getBillingUsage()
            .then(data => setUsage({ state: 'ok', data }))
            .catch(e => setUsage({ state: 'error', message: getErrorMessage(e) }));
    }, []);

    const loadInvoices = useCallback(() => {
        setInvoices({ state: 'loading' });
        apiClient.get<{ invoices: Invoice[] | null }>('/billing/invoices')
            .then(r => setInvoices({ state: 'ok', data: r.data?.invoices ?? [] }))
            .catch(e => setInvoices({ state: 'error', message: getErrorMessage(e) }));
    }, []);

    // Usage and invoices are fetched only after /billing succeeds, so a
    // non-admin (403) never requests them.
    const loadInfo = useCallback(() => {
        setInfo({ state: 'loading' });
        apiClient.get<BillingInfo>('/billing')
            .then(r => {
                setInfo({ state: 'ok', data: r.data });
                loadUsage();
                loadInvoices();
            })
            .catch((e: any) => {
                if (e?.response?.status === 403) setInfo({ state: 'forbidden' });
                else setInfo({ state: 'error', message: getErrorMessage(e) });
            });
    }, [loadUsage, loadInvoices]);

    useEffect(() => { loadInfo(); }, [loadInfo]);

    if (info.state === 'forbidden') {
        return (
            <div className="hub-page">
                <HubHeading
                    eyebrow="Workspace / Billing & usage"
                    title="Billing access is restricted."
                    description="Only workspace admins can manage billing."
                />
                <div className="hub-scope-notice" role="status">
                    No subscription, invoice, payment, or usage details are shared in this view.
                </div>
            </div>
        );
    }

    const loaded = info.state === 'ok' ? info.data : null;
    const pastDue = loaded?.billing_status === 'unpaid';

    return (
        <div className="hub-page">
            <HubHeading
                eyebrow="Workspace settings / Billing & usage"
                title="Billing & usage"
                description="Your plan, payment management and invoice history, from Stripe."
                action={loaded?.stripe_customer_id ? (
                    <button className="btn btn-secondary btn-md" onClick={portalSession.open} disabled={portalSession.busy} style={iconBtn}>
                        {portalSession.busy ? 'Opening billing portal…' : <><Settings size={14} aria-hidden="true" /> {pastDue ? 'Update payment method' : 'Manage subscription'}</>}
                    </button>
                ) : undefined}
            />
            {portalSession.error && <p role="alert" style={{ ...errorText, marginBottom: 'var(--space-4)' }}>Billing portal error: {portalSession.error}</p>}
            <ReturnBanner checkout={checkout} info={loaded} />
            {loaded && <WorkspacePlans />}
            {loaded?.plan_version === 'v1' && <RetainedCapacity />}
            {loaded?.plan_version === 'legacy' && <p>Your existing workspace keeps its agreed limits and subscription terms until you explicitly change plans.</p>}
            {loaded && <p>Current capacity: {loaded.project_limit ?? 'Unlimited'} active projects · {loaded.people_limit ?? 'Unlimited'} people (including reserved invitations).</p>}
            {loaded && <label>Choose your paid plan
                <select aria-label="Choose your paid plan" value={selectedPlan} onChange={event => setSelectedPlan(event.target.value)}>
                    <option value="team">Team — $19/workspace/month</option>
                    <option value="pro">Pro — $49/workspace/month</option>
                </select>
            </label>}


            <div className="hub-card-grid">
                <section className="hub-panel hub-card" aria-label="Subscription">
                    {info.state === 'loading' && <Loading what="billing" />}
                    {info.state === 'error' && <Failed what="billing" message={info.message} onRetry={loadInfo} />}
                    {loaded && <Subscription info={loaded} checkoutSession={checkoutSession} />}
                </section>

                {loaded && (
                    <section className="hub-panel hub-card" aria-label="Usage">
                        <p className="hub-eyebrow">Workspace usage</p>
                        <h2>Measured usage</h2>
                        {usage.state === 'loading' && <Loading what="usage" />}
                        {usage.state === 'error' && <Failed what="usage" message={usage.message} onRetry={loadUsage} />}
                        {usage.state === 'ok' && <UsageList u={usage.data} />}
                        <Link to="/admin/ai" className="hub-text-link">Workspace AI provider <ArrowUpRight size={14} aria-hidden="true" /></Link>
                    </section>
                )}
            </div>

            {loaded && (
                <section className="hub-panel" aria-labelledby="billing-invoices">
                    <div className="hub-panel-heading">
                        <h2 id="billing-invoices">Invoice history</h2>
                        <span className="hub-muted">From Stripe</span>
                    </div>
                    <div className="hub-panel-body">
                        {invoices.state === 'loading' && <Loading what="invoices" />}
                        {invoices.state === 'error' && <Failed what="invoices" message={invoices.message} onRetry={loadInvoices} />}
                        {invoices.state === 'ok' && invoices.data.length === 0 && <p style={muted}>No invoices.</p>}
                        {invoices.state === 'ok' && invoices.data.length > 0 && <InvoiceTable invoices={invoices.data} />}
                    </div>
                </section>
            )}

            {loaded && (
                <NoteBar>
                    Payment methods and invoices are managed in the Stripe customer portal. Workspace deletion lives
                    in <Link to="/settings/workspace#lifecycle">Settings › Data &amp; lifecycle</Link>.
                </NoteBar>
            )}
        </div>
    );
}

function Subscription({ info, checkoutSession }: {
    info: BillingInfo;
    checkoutSession: ReturnType<typeof useStripeRedirect>;
}) {
    const badge = STATUS_BADGE[info.billing_status];
    const canCheckout = true;
    return (
        <>
            <div className="hub-card-top">
                <p className="hub-eyebrow">Current subscription</p>
                <span className="hub-pill" data-tone={badge?.tone} data-testid="billing-status">{badge?.label ?? info.billing_status}</span>
            </div>
            <h2 data-testid="plan-name">{getPlan(info.plan).label}</h2>
            {info.billing_status === 'unpaid' && (
                <p role="alert" style={errorText}>Your last payment failed. Update your payment method in the billing portal.</p>
            )}
            <dl className="hub-facts">
                {info.trial_ends_at && <div><dt>Trial ends</dt><dd>{fmtDate(info.trial_ends_at)}</dd></div>}
                {info.paid_at && <div><dt>Last payment</dt><dd>{fmtDate(info.paid_at)}</dd></div>}
                <div><dt>Billing provider</dt><dd>Stripe</dd></div>
                <div><dt>Payment management</dt><dd>{info.stripe_customer_id ? 'Stripe customer portal' : 'No Stripe customer yet'}</dd></div>
            </dl>
            {canCheckout && (
                <button className="btn btn-primary btn-md" onClick={checkoutSession.open} disabled={checkoutSession.busy} style={iconBtn}>
                    {checkoutSession.busy ? 'Opening Stripe Checkout…' : <><Zap size={16} aria-hidden="true" /> Continue to Stripe Checkout</>}
                </button>
            )}
            {checkoutSession.error && <p role="alert" style={{ ...errorText, marginTop: 'var(--space-2)' }}>Checkout error: {checkoutSession.error}</p>}
        </>
    );
}

/** Concept invoice table: Invoice · Date · Amount · Status · Actions. */
function InvoiceTable({ invoices }: { invoices: Invoice[] }) {
    const th: React.CSSProperties = { padding: '0 12px 12px', fontWeight: 600, fontSize: 'var(--font-size-xs)', color: 'var(--ink-muted)', textAlign: 'left' };
    const td: React.CSSProperties = { padding: '14px 12px', borderTop: '1px solid var(--border)' };
    return (
        <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}>
                <thead>
                    <tr>
                        <th style={{ ...th, paddingLeft: 0 }}>Invoice</th>
                        <th style={th}>Date</th>
                        <th style={th}>Amount</th>
                        <th style={th}>Status</th>
                        <th style={{ ...th, paddingRight: 0, textAlign: 'right' }}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {invoices.map(inv => {
                        const amount = inv.status === 'paid' ? inv.amount_paid : inv.amount_due;
                        return (
                            <tr key={inv.id}>
                                <td style={{ ...td, paddingLeft: 0, fontWeight: 600, overflowWrap: 'anywhere' }}>{inv.number || inv.id}</td>
                                <td style={td}>{new Date(inv.created * 1000).toLocaleDateString()}</td>
                                <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{formatInvoiceAmount(amount, inv.currency)}</td>
                                <td style={td}><span className="hub-pill" data-tone={INVOICE_TONE[inv.status]} style={{ textTransform: 'capitalize' }}>{inv.status}</span></td>
                                <td style={{ ...td, paddingRight: 0, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                    {inv.hosted_invoice_url && <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm">View</a>}
                                    {inv.invoice_pdf && <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm" style={{ marginLeft: 4 }}>PDF</a>}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function UsageList({ u }: { u: BillingUsage }) {
    const measured = (n: number | null) => (n === null ? 'Not measured' : n.toLocaleString());
    const since = fmtDate(u.period_start);
    const rows: [string, string][] = [
        ['Members', u.users.toLocaleString()],
        ['Projects', u.projects.toLocaleString()],
        ['Attachment storage', formatBytes(u.storage_bytes)],
        [`AI calls since ${since}`, measured(u.ai_calls_mtd)],
        [`AI tokens since ${since}`, measured(u.ai_tokens_mtd)],
        [`MCP calls since ${since}`, measured(u.mcp_calls_mtd)],
    ];
    return (
        <>
            <dl className="hub-facts">
                {rows.map(([k, v]) => (
                    <div key={k}>
                        <dt>{k}</dt>
                        <dd style={v === 'Not measured' ? { color: 'var(--ink-muted)' } : undefined}>{v}</dd>
                    </div>
                ))}
            </dl>
            <p style={{ ...muted, fontSize: 'var(--font-size-xs)' }}>Measured {new Date(u.measured_at).toLocaleString()}.</p>
        </>
    );
}
